import {
  describeStorageError,
  reportStorageDenied,
  StorageApi,
} from './storageDeniedTelemetry'

/**
 * Web Storage access that degrades instead of throwing.
 *
 * A browser can refuse storage outright — enterprise policy on managed
 * Windows, storage partitioning, private browsing — and it refuses at a
 * place that is easy to guard wrongly: the throw comes from the
 * `window.localStorage` *property getter*, before any method is called
 * (`SecurityError`, `DOMException.code` 18). So the property read itself
 * has to sit inside the try; wrapping only `getItem` is not enough, and
 * caching `const ls = localStorage` at module scope reintroduces the
 * original crash.
 *
 * Reads degrade to `null` so callers fall back to their default. Writes
 * degrade to a no-op and report whether they persisted, so a caller that
 * cares can tell. Every distinct denial is reported once per session by
 * {@link reportStorageDenied} — the fallback must not be silent.
 */

function resolveStorage(api: StorageApi): Storage | null {
  try {
    // The property access is the throwing operation — it must be in here.
    return (globalThis[api] as Storage) ?? null
  } catch (error) {
    reportStorageDenied({
      api,
      stage: 'property',
      ...describeStorageError(error),
    })
    return null
  }
}

export function safeGetItem(
  key: string,
  api: StorageApi = 'localStorage',
): string | null {
  const storage = resolveStorage(api)
  if (!storage) {
    return null
  }

  try {
    return storage.getItem(key)
  } catch (error) {
    reportStorageDenied({
      api,
      stage: 'operation',
      ...describeStorageError(error),
    })
    return null
  }
}

/** @returns whether the value was actually persisted. */
export function safeSetItem(
  key: string,
  value: string,
  api: StorageApi = 'localStorage',
): boolean {
  const storage = resolveStorage(api)
  if (!storage) {
    return false
  }

  try {
    storage.setItem(key, value)
    return true
  } catch (error) {
    reportStorageDenied({
      api,
      stage: 'operation',
      ...describeStorageError(error),
    })
    return false
  }
}

/** @returns whether the key was actually removed. */
export function safeRemoveItem(
  key: string,
  api: StorageApi = 'localStorage',
): boolean {
  const storage = resolveStorage(api)
  if (!storage) {
    return false
  }

  try {
    storage.removeItem(key)
    return true
  } catch (error) {
    reportStorageDenied({
      api,
      stage: 'operation',
      ...describeStorageError(error),
    })
    return false
  }
}

/**
 * Read and parse a JSON-encoded entry. Several call sites did
 * `JSON.parse(localStorage.getItem(k))`, which can throw twice over — once
 * on the storage access and again on malformed content.
 *
 * A parse failure is a corrupt value, NOT a storage denial, so it is not
 * reported as one; it degrades to `null` the same way a missing key does.
 */
export function safeGetJson<T>(
  key: string,
  api: StorageApi = 'localStorage',
): T | null {
  const raw = safeGetItem(key, api)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}
